import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useAuth, useUser } from "@clerk/clerk-react";
import { FiCheck, FiX } from "react-icons/fi";
import confetti from "canvas-confetti";
import { showCreditRequestSuccess, showCreditRequestError, showWelcomeCreditsClaimSuccess } from "../../../utils/appToast";
import "./ClaimCredits.css";
import logger from "../../../utils/logger";
import { getBalance, requestMoreCredits } from "../../../services/api/app/credits";
import { claimSignupCredits } from "../../../services/api/app/user";
import { IS_APP_V1 } from "../../../config/app/appVersion";
import { BRAND_LOGO } from "../../../config/app/brandAssets";
import {
  SIGNUP_CREDITS,
  publishCreditsBalanceChanged,
} from "../../../config/app/signupCredits";

const buildBenefits = (amount) => [
  {
    title: `${amount} Welcome Credits`,
    description: "Perfect start to try our AI tools",
  },
  {
    title: "Credits Never Expire",
    description: "Use them anytime, at your own pace",
  },
  {
    title: "One-time Reward",
    description: "This welcome gift can only be claimed once",
  },
];

const CLAIM_CONFETTI_COLORS = [
  "#ff9500",
  "#ffb340",
  "#ff6b00",
  "#0066ff",
  "#00aaff",
  "#1a5cff",
  "#0a0a0a",
  "#2a2a2a",
  "#ffffff",
];

const fireClaimConfetti = () => {
  const base = {
    zIndex: 10060,
    colors: CLAIM_CONFETTI_COLORS,
  };

  confetti({
    ...base,
    particleCount: 90,
    spread: 75,
    origin: { y: 0.62, x: 0.5 },
  });

  confetti({
    ...base,
    particleCount: 45,
    spread: 90,
    origin: { y: 0.68, x: 0.32 },
  });

  confetti({
    ...base,
    particleCount: 45,
    spread: 90,
    origin: { y: 0.68, x: 0.68 },
  });
};

const ClaimCredits = ({
  onClose,
  onClaimed,
  skipMarkWelcomed = false,
  isOutOfCredits = false,
}) => {
  const [creditsBalance, setCreditsBalance] = useState(0);
  const [isClaiming, setIsClaiming] = useState(false);
  const [claimError, setClaimError] = useState("");
  const { getToken } = useAuth();
  const { user } = useUser();

  const firstName = (user?.firstName || "").trim();
  const greeting = firstName ? `Hi ${firstName}` : "";
  const benefits = buildBenefits(SIGNUP_CREDITS);

  useEffect(() => {
    if (IS_APP_V1 || isOutOfCredits) return;

    const fetchBalance = async () => {
      try {
        const token = await getToken();
        if (!token) return;
        const data = await getBalance(token);
        setCreditsBalance(data.credits ?? 0);
      } catch (error) {
        logger.error("Failed to fetch credits balance", error);
      }
    };

    fetchBalance();
  }, [getToken, isOutOfCredits]);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  const handleClaim = async () => {
    if (isClaiming) return;

    setIsClaiming(true);
    setClaimError("");

    try {
      let creditsGranted = skipMarkWelcomed ? SIGNUP_CREDITS : 0;

      if (!skipMarkWelcomed) {
        const token = await getToken();
        if (!token) {
          throw new Error("Sign in required to claim credits.");
        }
        const result = await claimSignupCredits(token);
        creditsGranted = result?.creditsGranted ?? 0;

        if (creditsGranted <= 0) {
          throw new Error("Welcome credits were already claimed for this account.");
        }

        publishCreditsBalanceChanged(result?.newBalance);
      }

      fireClaimConfetti();

      if (onClaimed) {
        onClaimed();
      }

      setTimeout(() => {
        onClose();
        showWelcomeCreditsClaimSuccess(creditsGranted);
      }, 900);
    } catch (error) {
      logger.error("Failed to claim signup credits", error);
      setClaimError("Could not claim credits. Please try again.");
      setIsClaiming(false);
    }
  };

  const handleRequestMoreCredits = async () => {
    if (isClaiming) return;

    setIsClaiming(true);
    setClaimError("");

    try {
      const token = await getToken();
      if (!token) {
        throw new Error("Sign in required to request credits.");
      }

      await requestMoreCredits(token);
      showCreditRequestSuccess();
      onClose();
    } catch (error) {
      logger.error("Failed to request more credits", error);
      showCreditRequestError();
      setIsClaiming(false);
    }
  };

  const progressPercentage = Math.min((creditsBalance / 2000) * 100, 100);

  const modalTitleId = isOutOfCredits
    ? "out-of-credits-title"
    : "claim-credits-title";

  return createPortal(
    <div
      className="claim-credits-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={modalTitleId}
    >
      <div className="claim-credits-modal">
        {isOutOfCredits && (
          <button
            type="button"
            className="claim-close-btn"
            onClick={onClose}
            aria-label="Close"
            disabled={isClaiming}
          >
            <FiX aria-hidden="true" />
          </button>
        )}

        <div className="claim-hero">
          <div className="claim-icon">
            <img
              src={BRAND_LOGO.favicon}
              alt=""
              className="claim-icon-image"
              aria-hidden="true"
            />
          </div>

          {greeting ? (
            <p className="claim-subtitle claim-user-name">{greeting}</p>
          ) : null}

          {isOutOfCredits ? (
            <>
              <h1 className="claim-title" id={modalTitleId}>
                Thanks for trying LazyStudio.Ai
              </h1>
              <p className="claim-subtitle">
                You&apos;ve used all your 10 free credits.
                <br />
                We&apos;re in early access — more credits and payments are coming soon.
              </p>
            </>
          ) : (
            <>
              <h1 className="claim-title" id={modalTitleId}>
                Welcome to LazyStudio.Ai
              </h1>
              <p className="claim-subtitle">Your account has been created successfully.</p>
            </>
          )}
        </div>

        <div className="claim-credits-card">
          {!isOutOfCredits && (
            <>
              <div className="claim-credits-display">
                <span className="claim-credits-amount">{SIGNUP_CREDITS}</span>
                <p className="claim-unlock-line">
                  You&apos;ve unlocked <strong>FREE Credits</strong>
                </p>
              </div>

              <p className="claim-unlock-copy">
                Turn your ideas into stunning visuals with AI.
                <br />
                Use your welcome credits to explore our creative tools.
              </p>
            </>
          )}

          <button
            type="button"
            className="claim-now-btn"
            onClick={isOutOfCredits ? handleRequestMoreCredits : handleClaim}
            disabled={isClaiming}
          >
            {isClaiming
              ? isOutOfCredits
                ? "Sending..."
                : "Claiming..."
              : isOutOfCredits
                ? "Request More Credits"
                : `Claim ${SIGNUP_CREDITS} Credits`}
          </button>

          {claimError && <p className="claim-error">{claimError}</p>}
        </div>

        {!IS_APP_V1 && !isOutOfCredits && (
          <div className="claim-balance-section">
            <p className="balance-title">YOUR CREDITS BALANCE</p>
            <div className="balance-progress">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${progressPercentage}%` }}
                ></div>
                <div className="progress-marker" style={{ left: `${progressPercentage}%` }}>
                  <div className="marker-dot"></div>
                  <div className="marker-label">{creditsBalance}</div>
                </div>
              </div>
              <div className="progress-labels">
                <span>0</span>
                <span>500</span>
                <span>1000</span>
                <span>2000</span>
              </div>
            </div>
          </div>
        )}

        {!isOutOfCredits && (
          <div className="claim-benefits">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="claim-benefit-row">
                <FiCheck className="claim-benefit-check" aria-hidden="true" />
                <p className="claim-benefit-line">
                  <strong>{benefit.title}:</strong> {benefit.description}
                </p>
              </div>
            ))}
          </div>
        )}

        {!isOutOfCredits && (
          <p className="claim-footer-note">
            Welcome Credits can only be claimed once per account.
          </p>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ClaimCredits;
